import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import { saudeService } from '../../services/api';
import { getClassificacaoColor } from './ClassificacaoBadge';

const COR_PROJETADO = '#1565c0';
const COR_POTENCIAL = '#2e7d32';

const formatCurrency = (value: number): string =>
  (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface ComponenteQualidadeCardProps {
  municipioId: number;
  quadrimestre: string;
  unidade?: string;
  municipioNome?: string;
  reloadToken?: number;
  /**
   * Fonte dos dados (Consolidado x Tempo real). Serve so de GATILHO do refetch: a fonte em si
   * viaja no `fonte=` que o interceptor do axios anexa a /saude/indicadores/. Sem ela nas deps
   * do efeito, alternar a fonte recarregava os eixos e deixava este card exibindo o numero da
   * fonte anterior, sem erro e sem aviso.
   */
  fonte?: string;
}

const ComponenteQualidadeCard: React.FC<ComponenteQualidadeCardProps> = ({
  municipioId,
  quadrimestre,
  unidade,
  municipioNome,
  reloadToken,
  fonte,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<any>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!municipioId) return;
    let ativo = true;
    setLoading(true);
    setError(false);
    saudeService.getSimulacaoFinanceira(municipioId, quadrimestre, unidade)
      .then((res) => { if (ativo) { setData(res); setLoading(false); } })
      .catch(() => { if (ativo) { setError(true); setLoading(false); } });
    return () => { ativo = false; };
  }, [municipioId, quadrimestre, unidade, fonte, reloadToken]);

  // Extrai o componente Qualidade do retorno da simulacao financeira
  const q = useMemo(
    () => data?.componentes?.find((c: any) => (c.componente || '').startsWith('Qualidade')),
    [data],
  );

  const projetadoMes = q?.totalMensal ?? 0;
  const potencialMes = q?.totalMaximo ?? 0;
  const gapMes = Math.max(0, potencialMes - projetadoMes);
  const aproveitamento = potencialMes > 0 ? Math.round((projetadoMes / potencialMes) * 100) : 0;
  const temDados = !!q && Array.isArray(q.equipes) && q.equipes.length > 0;

  const LARGURA = '100%';

  // --- Estados de carregamento / indisponivel ---
  if (loading) {
    return (
      <Box sx={{ width: LARGURA }}>
        <Skeleton variant="text" width={160} height={22} />
        <Skeleton variant="text" width={140} height={40} />
        <Skeleton variant="rounded" width="100%" height={8} sx={{ my: 1 }} />
        <Skeleton variant="text" width={120} height={18} />
      </Box>
    );
  }

  if (error || !temDados) {
    return (
      <Box sx={{ width: LARGURA }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <AccountBalanceIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            Componente Qualidade
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Dados de financiamento indisponíveis.
        </Typography>
      </Box>
    );
  }

  const tituloModal = `Componente Qualidade${municipioNome ? ` — ${municipioNome}` : ''} · ${quadrimestre}`;

  return (
    <>
      {/* Card sucinto (substitui o gauge no hero) */}
      <Box
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
        sx={{
          width: LARGURA,
          cursor: 'pointer',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          p: 1.75,
          transition: 'border-color .15s, box-shadow .15s',
          '&:hover': { borderColor: COR_PROJETADO, boxShadow: `0 0 0 1px ${alpha(COR_PROJETADO, 0.3)}` },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
          <AccountBalanceIcon sx={{ fontSize: 18, color: COR_PROJETADO }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
            Componente Qualidade
          </Typography>
          <Tooltip title="Estimativa do repasse mensal do Componente Qualidade do cofinanciamento federal da APS, com base na classificação das equipes. Clique para ver o detalhamento.">
            <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', mt: 0.5 }}>
          {/* Numero principal */}
          <Box>
            <Typography variant="caption" color="text.secondary">receita estimada</Typography>
            <Typography variant="h4" fontWeight={800} sx={{ color: COR_PROJETADO, lineHeight: 1.1 }}>
              {formatCurrency(projetadoMes)}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>/mês</Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: COR_POTENCIAL, mt: 0.25 }}>
              potencial {formatCurrency(potencialMes)}/mês
            </Typography>
          </Box>

          {/* Aproveitamento + gap */}
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {aproveitamento}% do potencial
              </Typography>
              <Typography variant="caption" sx={{ color: COR_PROJETADO, fontWeight: 600 }}>
                ver detalhes
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, aproveitamento)}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(COR_POTENCIAL, 0.15),
                '& .MuiLinearProgress-bar': { bgcolor: COR_PROJETADO, borderRadius: 4 },
              }}
            />
            {gapMes > 0 && (
              <Chip
                size="small"
                label={`${formatCurrency(gapMes)}/mês não aproveitado`}
                sx={{ mt: 1, bgcolor: alpha('#e65100', 0.1), color: '#e65100', fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Modal de detalhamento */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <AccountBalanceIcon sx={{ color: COR_PROJETADO }} />
          <Typography variant="h6" fontWeight={700} component="span">{tituloModal}</Typography>
          <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* 4 mini-cards de totais */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
            <MiniCard rotulo="Projetado/mês" valor={formatCurrency(projetadoMes)} anual={`${formatCurrency(projetadoMes * 12)}/ano`} cor={COR_PROJETADO} />
            <MiniCard rotulo="Potencial/mês" valor={formatCurrency(potencialMes)} anual={`${formatCurrency(potencialMes * 12)}/ano`} cor={COR_POTENCIAL} />
            <MiniCard rotulo="Gap/mês" valor={formatCurrency(gapMes)} anual={`${formatCurrency(gapMes * 12)}/ano`} cor="#e65100" />
            <MiniCard rotulo="Aproveitamento" valor={`${aproveitamento}%`} anual="do potencial" cor={theme.palette.text.primary} icon={<TrendingUpIcon sx={{ fontSize: 16 }} />} />
          </Box>

          {/* Tabela por tipo de equipe */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="center">Equipes</TableCell>
                  <TableCell align="center">Classificação</TableCell>
                  <TableCell align="right">Valor/Equipe</TableCell>
                  <TableCell align="right">Receita Mensal</TableCell>
                  <TableCell align="right">Valor Máx</TableCell>
                  <TableCell align="right">Receita Máx</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {q.equipes.map((eq: any) => (
                  <TableRow key={`${eq.tipo}-${eq.tipoMs}`}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{eq.tipo}</Typography></TableCell>
                    <TableCell align="center">{eq.quantidade}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={eq.classificacao}
                        size="small"
                        sx={{
                          bgcolor: alpha(getClassificacaoColor(eq.classificacao), 0.12),
                          color: getClassificacaoColor(eq.classificacao),
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(eq.valorUnitario)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(eq.receitaMensal)}</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatCurrency(eq.valorMaximo)}</TableCell>
                    <TableCell align="right" sx={{ color: COR_POTENCIAL }}>{formatCurrency(eq.receitaMaxima)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { borderTop: `2px solid ${theme.palette.divider}` } }}>
                  <TableCell colSpan={4}><Typography variant="body2" fontWeight={700}>Total</Typography></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(projetadoMes)}</TableCell>
                  <TableCell />
                  <TableCell align="right" sx={{ fontWeight: 700, color: COR_POTENCIAL }}>{formatCurrency(potencialMes)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          {/* Caveat metodologico + fonte */}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
            Estimativa baseada na classificação calculada pelo sistema e no subtipo padrão de equipe.
            O valor oficial é definido pelo Ministério da Saúde via SISAB.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Fonte: Portaria GM/MS nº 3.493/2024, atualizada por nº 6.907/2025 e nº 9.591/2025.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            onClick={() => { setOpen(false); navigate('/saude/financeiro'); }}
          >
            Abrir Simulador Financeiro completo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface MiniCardProps {
  rotulo: string;
  valor: string;
  anual: string;
  cor: string;
  icon?: React.ReactNode;
}

const MiniCard: React.FC<MiniCardProps> = ({ rotulo, valor, anual, cor, icon }) => {
  const theme = useTheme();
  return (
    <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 1.25 }}>
      <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
      <Typography variant="h6" fontWeight={800} sx={{ color: cor, lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon}{valor}
      </Typography>
      <Typography variant="caption" color="text.secondary">{anual}</Typography>
    </Box>
  );
};

export default ComponenteQualidadeCard;
